import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import withRouter from "../Common/withRouter"
import { withTranslation } from "react-i18next"
import { ICONS } from "../../constants/icons"
import { get, post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { mapApiProject } from "../../pages/Projects/ProjectsListing"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"
import useInfinitePagination from "../../hooks/useInfinitePagination"
import OnlineStatus from "../OnlineStatus"
import { useDirectContactUpdates } from "../../providers/DirectContactsProvider"
import { useChatDock } from "../DirectChat"

const DM_PAGE_SIZE = 10
const PM_PAGE_SIZE = 10
const DM_SEARCH_DEBOUNCE_MS = 300
const PM_SEARCH_DEBOUNCE_MS = 300
const RECENT_PROJECTS_SKELETON_COUNT = 5
const DIRECT_CONTACTS_SKELETON_COUNT = 4

const getProjectIdFromPath = pathname => {
  const match = String(pathname || "").match(/^\/projects\/([^/]+)/)
  const id = match?.[1]
  if (!id || id === "create") return null
  return id
}

const getRandomColor = seed => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 85%, 90%)`
}

const getRandomTextColor = seed => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 90%, 40%)`
}

const getContactDisplayName = contact => {
  const participant = contact?.participants?.[0]
  return (
    getUserFullName(participant) ||
    contact?.title ||
    contact?.subject ||
    "Unknown"
  )
}

const getContactInitials = name =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?"

const getContactProjectName = contact => {
  const project = contact?.project
  const name =
    (typeof project === "string" && project) ||
    project?.name ||
    project?.title ||
    contact?.project_name ||
    contact?.projectName ||
    contact?.project_title ||
    ""
  return String(name).trim()
}

const normalizeContactPerson = person => {
  if (!person || typeof person !== "object") return null
  const nested =
    (person.user && typeof person.user === "object" && person.user) || null
  const user = nested
    ? { ...person, ...nested, id: nested.id || person.id }
    : person
  const name = getUserFullName(user)
  const avatarUrl = getUserAvatarUrl(user)
  return {
    id: user.id,
    name,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    avatar: avatarUrl || "",
  }
}

const mapDirectContact = (contact, currentUserId, currentUser = null) => {
  const rawPeople = Array.isArray(contact?.participants)
    ? contact.participants
    : Array.isArray(contact?.collaborators)
    ? contact.collaborators
    : []
  const participants = rawPeople.map(normalizeContactPerson).filter(Boolean)

  const participantCount =
    Number(contact?.participants_count) ||
    Number(contact?.collaborators_count) ||
    participants.length
  const isGroupFlag =
    contact?.is_group === true ||
    contact?.is_group === 1 ||
    contact?.is_group === "1" ||
    contact?.is_group === "true" ||
    contact?.isGroup === true ||
    contact?.isGroup === 1 ||
    contact?.isGroup === "true"
  const isGroup = Boolean(
    isGroupFlag || contact?.context_type === "group" || participantCount > 2
  )

  const otherParticipant = participants.find(
    person => String(person?.id) !== String(currentUserId || "")
  )
  // Never use last_message.sender when it is the current user — that flips the
  // DM avatar/name to "you" (or the other party) on every send/receive update.
  const senderPerson = normalizeContactPerson(contact?.last_message?.sender)
  const senderAsOther =
    senderPerson &&
    String(senderPerson.id || "") !== String(currentUserId || "")
      ? senderPerson
      : null
  const participant = isGroup ? null : otherParticipant || senderAsOther || null

  const subject = String(contact?.subject || "").trim()
  const personName = getContactDisplayName({
    ...contact,
    participants: participant ? [participant] : participants,
  })
  const name = isGroup ? subject || personName || "Group chat" : personName

  const currentFace = currentUser ? normalizeContactPerson(currentUser) : null
  const faces = []
  const seen = new Set()
  const pushFace = person => {
    if (!person) return
    const key =
      person.id != null
        ? String(person.id)
        : `${person.name || ""}-${faces.length}`
    if (seen.has(key)) return
    seen.add(key)
    faces.push(person)
  }
  if (isGroup) {
    if (
      currentFace &&
      !participants.some(
        person => String(person.id) === String(currentFace.id || "")
      )
    ) {
      pushFace(currentFace)
    }
    participants.forEach(pushFace)
  }

  return {
    id: contact?.id,
    projectId: contact?.project_id || contact?.project?.id || null,
    projectName: getContactProjectName(contact),
    userId: isGroup ? null : participant?.id || null,
    name,
    initials: getContactInitials(name),
    avatar: getUserAvatarUrl(participant) || participant?.avatar || "",
    isGroup,
    subject,
    participants: isGroup
      ? faces.slice(0, 4)
      : participant
      ? [participant]
      : [],
    hasUnread: Boolean(contact?.hasUnread),
  }
}

/** Keep list identity stable across thin realtime payloads. */
const mergeDirectContactIdentity = (existing, mapped, currentUserId) => {
  if (!existing) return mapped

  const mappedIsSelf =
    !mapped.isGroup &&
    mapped.userId != null &&
    currentUserId != null &&
    String(mapped.userId) === String(currentUserId)

  const preferExistingIdentity =
    mappedIsSelf ||
    (!mapped.userId && existing.userId) ||
    (!mapped.avatar && existing.avatar) ||
    (!mapped.name && existing.name) ||
    (!(mapped.participants || []).length &&
      (existing.participants || []).length > 0)

  if (!preferExistingIdentity) {
    return {
      ...mapped,
      avatar: mapped.avatar || existing.avatar || "",
      name: mapped.name || existing.name,
      userId: mapped.userId || existing.userId || null,
      initials: mapped.initials || existing.initials,
      participants: (mapped.participants || []).length
        ? mapped.participants
        : existing.participants || [],
      projectName: mapped.projectName || existing.projectName || "",
      isGroup: Boolean(mapped.isGroup || existing.isGroup),
    }
  }

  return {
    ...mapped,
    name: existing.name || mapped.name,
    avatar: existing.avatar || mapped.avatar || "",
    userId: existing.userId || mapped.userId || null,
    initials: existing.initials || mapped.initials,
    participants: (existing.participants || []).length
      ? existing.participants
      : mapped.participants || [],
    isGroup: Boolean(existing.isGroup || mapped.isGroup),
    projectName: mapped.projectName || existing.projectName || "",
  }
}

const applyProjectNames = (contacts, names = {}) =>
  contacts.map(contact => {
    if (contact.projectName || !contact.projectId) return contact
    const name = names[String(contact.projectId)]
    return name ? { ...contact, projectName: name } : contact
  })

/** True 1:1 floating DM (no project) — not Project Messages. */
const isStandaloneDirectChat = (mapped, raw = null) => {
  if (!mapped || mapped.isGroup) return false
  const projectId =
    mapped.projectId || raw?.project_id || raw?.project?.id || null
  if (projectId) return false
  const contextType = String(raw?.context_type || "").toLowerCase()
  if (contextType === "group") return false
  if (contextType === "direct") return true
  return Boolean(mapped.userId)
}

/** Sidebar Direct Messages list from /project-members/my-members (name only). */
const mapMyMemberContact = (row, currentUserId) => {
  const user = row?.user && typeof row.user === "object" ? row.user : null
  const person = normalizeContactPerson(user)
  if (!person?.id) return null
  if (
    currentUserId != null &&
    String(person.id) === String(currentUserId)
  ) {
    return null
  }

  const name = person.name || "Unknown"
  const contextId = row?.last_message_context_id || null
  const memberId = row?.member_id || null

  return {
    id: contextId || memberId || person.id,
    memberId,
    contextId,
    projectId: null,
    projectName: "",
    userId: person.id,
    name,
    initials: getContactInitials(name),
    avatar: getUserAvatarUrl(user) || "",
    isGroup: false,
    subject: "",
    participants: [person],
    hasUnread: false,
  }
}

const SidebarContent = () => {
  const [projectsExpanded, setProjectsExpanded] = useState(true)
  const [dmExpanded, setDmExpanded] = useState(true)
  const [pmExpanded, setPmExpanded] = useState(true)
  const [recentProjects, setRecentProjects] = useState([])
  const [dmMembers, setDmMembers] = useState([])
  const [directContacts, setDirectContacts] = useState([])
  const [isRecentProjectsLoading, setIsRecentProjectsLoading] = useState(true)
  const [isDmMembersLoading, setIsDmMembersLoading] = useState(true)
  const [isDmMembersLoadingMore, setIsDmMembersLoadingMore] = useState(false)
  const [dmPage, setDmPage] = useState(1)
  const [dmHasMore, setDmHasMore] = useState(false)
  const [isDirectContactsLoading, setIsDirectContactsLoading] = useState(true)
  const [isDirectContactsLoadingMore, setIsDirectContactsLoadingMore] =
    useState(false)
  const [pmPage, setPmPage] = useState(1)
  const [pmHasMore, setPmHasMore] = useState(false)
  const [projectSearchOpen, setProjectSearchOpen] = useState(false)
  const [projectSearchQuery, setProjectSearchQuery] = useState("")
  const [dmSearchOpen, setDmSearchOpen] = useState(false)
  const [dmSearchQuery, setDmSearchQuery] = useState("")
  const [debouncedDmSearch, setDebouncedDmSearch] = useState("")
  const [pmSearchOpen, setPmSearchOpen] = useState(false)
  const [pmSearchQuery, setPmSearchQuery] = useState("")
  const [debouncedPmSearch, setDebouncedPmSearch] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)
  const [isSidebarCompact, setIsSidebarCompact] = useState(
    () =>
      typeof document !== "undefined" &&
      document.body.getAttribute("data-sidebar-size") === "sm"
  )
  const projectNameCacheRef = useRef({})
  const dmFetchIdRef = useRef(0)
  const pmFetchIdRef = useRef(0)
  const dmListRef = useRef(null)
  const pmListRef = useRef(null)
  const openChatsRef = useRef([])

  const location = useLocation()
  const navigate = useNavigate()
  const currentUserId = useSelector(state => state.auth?.currentUser?.id)
  const currentUser = useSelector(state => state.auth?.currentUser)
  const {
    subscribe: subscribeDirectContactUpdates,
    subscribeChatMembersUpdated,
  } = useDirectContactUpdates()
  const { openDirectChat: openFloatingDirectChat, openChats } = useChatDock()

  openChatsRef.current = openChats || []

  const isExpandedDmDockOpen = useCallback(contact => {
    const contextId = String(contact?.contextId || contact?.id || "")
    const userId = contact?.userId != null ? String(contact.userId) : ""
    return openChatsRef.current.some(chat => {
      if (chat.minimized) return false
      if (contextId && String(chat.id) === contextId) return true
      if (contextId && String(chat.contextId || "") === contextId) return true
      if (userId && String(chat.userId || "") === userId) return true
      return false
    })
  }, [])

  useEffect(() => {
    const syncSidebarSize = () => {
      setIsSidebarCompact(
        document.body.getAttribute("data-sidebar-size") === "sm"
      )
    }
    syncSidebarSize()
    const observer = new MutationObserver(syncSidebarSize)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-sidebar-size", "class"],
    })
    return () => observer.disconnect()
  }, [])

  // Compact rail hides section toggles — always show lists there.
  const showProjectsList = isSidebarCompact || projectsExpanded
  const showDmList = isSidebarCompact || dmExpanded
  const showPmList = isSidebarCompact || pmExpanded

  const fetchProjectNamesByIds = useCallback(async ids => {
    const unique = [...new Set((ids || []).filter(Boolean).map(String))]
    const missing = unique.filter(id => !projectNameCacheRef.current[id])

    if (missing.length) {
      const fetched = await Promise.all(
        missing.map(async id => {
          try {
            const response = await get(endpoints.getProjectById(id), {
              skipErrorToast: true,
            })
            const project = response?.data?.result
            const name = String(project?.name || project?.title || "").trim()
            return [id, name]
          } catch {
            return [id, ""]
          }
        })
      )
      fetched.forEach(([id, name]) => {
        if (name) projectNameCacheRef.current[id] = name
      })
    }

    return projectNameCacheRef.current
  }, [])

  const fetchRecentProjects = async () => {
    setIsRecentProjectsLoading(true)
    try {
      const response = await get(endpoints.getProjects, {
        params: {
          page: 1,
          page_size: 5,
          status: "present",
          sort_by: "created_at",
          sort_direction: "desc",
        },
      })
      if (response?.data?.success) {
        const list = (response.data.result?.data || []).map(project => {
          const mapped = mapApiProject(project)
          if (mapped.id && mapped.title) {
            projectNameCacheRef.current[String(mapped.id)] = mapped.title
          }
          return {
            id: mapped.id,
            initials: mapped.code,
            name: mapped.title,
            backgroundColor: getRandomColor(mapped.title),
            textColor: getRandomTextColor(mapped.title),
          }
        })
        setRecentProjects(list)
        setDirectContacts(prev =>
          applyProjectNames(prev, projectNameCacheRef.current)
        )
        return
      }
      setRecentProjects([])
    } catch {
      setRecentProjects([])
    } finally {
      setIsRecentProjectsLoading(false)
    }
  }

  const mapMyMemberRows = useCallback(
    rows => {
      const seen = new Set()
      const contacts = []
      ;(rows || []).forEach(row => {
        const mapped = mapMyMemberContact(row, currentUserId)
        if (!mapped?.userId) return
        const key = String(mapped.userId)
        if (seen.has(key)) return
        seen.add(key)
        contacts.push(mapped)
      })
      return contacts
    },
    [currentUserId]
  )

  const fetchDmMembers = useCallback(
    async ({ page = 1, append = false, search = "" } = {}) => {
      const fetchId = ++dmFetchIdRef.current
      if (append) setIsDmMembersLoadingMore(true)
      else setIsDmMembersLoading(true)

      try {
        const response = await get(endpoints.getMyProjectMembers, {
          params: {
            page,
            page_size: DM_PAGE_SIZE,
            search: search || "",
          },
          skipErrorToast: true,
        })

        if (fetchId !== dmFetchIdRef.current) return

        if (response?.data?.success) {
          const result = response.data.result || {}
          const contacts = mapMyMemberRows(result.data || [])
          const current = Number(result.current_page || page)
          const last = Number(result.last_page || current)

          setDmPage(current)
          setDmHasMore(current < last)
          setDmMembers(prev => {
            const prevByUser = new Map(
              prev.map(item => [String(item.userId), item])
            )
            const mergeRow = contact => {
              const old = prevByUser.get(String(contact.userId))
              if (!old) return contact
              const contextId = contact.contextId || old.contextId || null
              return {
                ...contact,
                memberId: contact.memberId || old.memberId || null,
                contextId,
                id: contextId || contact.id || old.id,
                hasUnread: Boolean(contact.hasUnread || old.hasUnread),
                avatar: contact.avatar || old.avatar || "",
                name: contact.name || old.name,
                initials: contact.initials || old.initials,
              }
            }
            if (!append) return contacts.map(mergeRow)
            const existing = new Set(prev.map(item => String(item.userId)))
            const merged = [...prev]
            contacts.forEach(item => {
              if (existing.has(String(item.userId))) return
              existing.add(String(item.userId))
              merged.push(mergeRow(item))
            })
            return merged
          })
          return
        }

        if (!append) {
          setDmMembers([])
          setDmPage(1)
          setDmHasMore(false)
        }
      } catch {
        if (fetchId !== dmFetchIdRef.current) return
        if (!append) {
          setDmMembers([])
          setDmPage(1)
          setDmHasMore(false)
        }
      } finally {
        if (fetchId === dmFetchIdRef.current) {
          setIsDmMembersLoading(false)
          setIsDmMembersLoadingMore(false)
        }
      }
    },
    [mapMyMemberRows]
  )

  const fetchDirectContacts = useCallback(
    async ({ page = 1, append = false, search = "" } = {}) => {
      const fetchId = ++pmFetchIdRef.current
      if (append) setIsDirectContactsLoadingMore(true)
      else setIsDirectContactsLoading(true)

      try {
        const response = await get(endpoints.getDirectContacts, {
          params: {
            page,
            page_size: PM_PAGE_SIZE,
            per_page: PM_PAGE_SIZE,
            search: search || "",
            "with[]": ["project", "collaborators", "participants"],
          },
          skipErrorToast: true,
        })

        if (fetchId !== pmFetchIdRef.current) return

        if (response?.data?.success) {
          const result = response.data.result || {}
          const contacts = (result.data || [])
            .map(contact =>
              mapDirectContact(contact, currentUserId, currentUser)
            )
            .filter(
              contact =>
                contact.id && (contact.projectId || contact.isGroup)
            )
            .map(contact => ({
              ...contact,
              projectName:
                contact.projectName ||
                projectNameCacheRef.current[String(contact.projectId || "")] ||
                "",
            }))

          const current = Number(result.current_page || page)
          const last = Number(result.last_page || current)
          setPmPage(current)
          setPmHasMore(current < last)
          setDirectContacts(prev => {
            if (!append) return contacts
            const existing = new Set(prev.map(item => String(item.id)))
            const merged = [...prev]
            contacts.forEach(item => {
              if (existing.has(String(item.id))) return
              existing.add(String(item.id))
              merged.push(item)
            })
            return merged
          })

          const names = await fetchProjectNamesByIds(
            contacts.map(contact => contact.projectId)
          )
          if (fetchId !== pmFetchIdRef.current) return
          setDirectContacts(prev => applyProjectNames(prev, names))
          return
        }

        if (!append) {
          setDirectContacts([])
          setPmPage(1)
          setPmHasMore(false)
        }
      } catch {
        if (fetchId !== pmFetchIdRef.current) return
        if (!append) {
          setDirectContacts([])
          setPmPage(1)
          setPmHasMore(false)
        }
      } finally {
        if (fetchId === pmFetchIdRef.current) {
          setIsDirectContactsLoading(false)
          setIsDirectContactsLoadingMore(false)
        }
      }
    },
    [currentUser, currentUserId, fetchProjectNamesByIds]
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDmSearch(dmSearchQuery.trim())
    }, DM_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [dmSearchQuery])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPmSearch(pmSearchQuery.trim())
    }, PM_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [pmSearchQuery])

  useEffect(() => {
    fetchRecentProjects()
  }, [refreshKey])

  useEffect(() => {
    fetchDmMembers({
      page: 1,
      append: false,
      search: debouncedDmSearch,
    })
  }, [refreshKey, debouncedDmSearch, fetchDmMembers])

  useEffect(() => {
    fetchDirectContacts({
      page: 1,
      append: false,
      search: debouncedPmSearch,
    })
  }, [refreshKey, debouncedPmSearch, fetchDirectContacts])

  const loadMoreDmMembers = useCallback(() => {
    if (isDmMembersLoading || isDmMembersLoadingMore || !dmHasMore) return
    fetchDmMembers({
      page: dmPage + 1,
      append: true,
      search: debouncedDmSearch,
    })
  }, [
    debouncedDmSearch,
    dmHasMore,
    dmPage,
    fetchDmMembers,
    isDmMembersLoading,
    isDmMembersLoadingMore,
  ])

  const loadMoreDirectContacts = useCallback(() => {
    if (isDirectContactsLoading || isDirectContactsLoadingMore || !pmHasMore)
      return
    fetchDirectContacts({
      page: pmPage + 1,
      append: true,
      search: debouncedPmSearch,
    })
  }, [
    debouncedPmSearch,
    fetchDirectContacts,
    isDirectContactsLoading,
    isDirectContactsLoadingMore,
    pmHasMore,
    pmPage,
  ])

  const { sentinelRef: dmSentinelRef } = useInfinitePagination({
    hasMore: dmHasMore,
    isLoading: isDmMembersLoading || isDmMembersLoadingMore,
    onLoadMore: loadMoreDmMembers,
    enabled: showDmList,
    rootRef: dmListRef,
    resetKey: `${debouncedDmSearch}:${dmPage}:${dmMembers.length}`,
  })

  const { sentinelRef: pmSentinelRef } = useInfinitePagination({
    hasMore: pmHasMore,
    isLoading: isDirectContactsLoading || isDirectContactsLoadingMore,
    onLoadMore: loadMoreDirectContacts,
    enabled: showPmList,
    rootRef: pmListRef,
    resetKey: `${debouncedPmSearch}:${pmPage}:${directContacts.length}`,
  })

  useEffect(() => {
    const handleProjectUpdate = () => {
      setRefreshKey(prev => prev + 1)
    }

    window.addEventListener("project-updated", handleProjectUpdate)
    window.addEventListener("project-created", handleProjectUpdate)

    return () => {
      window.removeEventListener("project-updated", handleProjectUpdate)
      window.removeEventListener("project-created", handleProjectUpdate)
    }
  }, [])

  useEffect(() => {
    const handleContactDeleted = e => {
      const deletedId = e?.detail?.id
      if (!deletedId) return
      setDirectContacts(prev =>
        prev.filter(contact => String(contact.id) !== String(deletedId))
      )
    }

    window.addEventListener("direct-contact-deleted", handleContactDeleted)
    return () => {
      window.removeEventListener("direct-contact-deleted", handleContactDeleted)
    }
  }, [])

  useEffect(() => {
    const handleProjectArchived = e => {
      const archivedProjectId = e?.detail?.projectId
      if (!archivedProjectId) return
      setDirectContacts(prev =>
        prev.filter(
          contact => String(contact.projectId) !== String(archivedProjectId)
        )
      )
      setRecentProjects(prev =>
        prev.filter(p => String(p.id) !== String(archivedProjectId))
      )
    }

    const handleProjectRestored = () => {
      // Re-fetch so restored project's chats reappear without a full reload
      setRefreshKey(prev => prev + 1)
    }

    window.addEventListener("project-archived", handleProjectArchived)
    window.addEventListener("project-restored", handleProjectRestored)
    return () => {
      window.removeEventListener("project-archived", handleProjectArchived)
      window.removeEventListener("project-restored", handleProjectRestored)
    }
  }, [])

  const filteredProjects = recentProjects.filter(
    project =>
      project.name.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
      project.initials.toLowerCase().includes(projectSearchQuery.toLowerCase())
  )

  // Direct Messages = my-members API (server search + infinite scroll).
  // Project Messages = direct contacts API (server search + infinite scroll).
  const filteredDirectUsers = dmMembers
  const filteredProjectUsers = directContacts

  const isProjectActive = projectId =>
    location.pathname.startsWith(`/projects/${projectId}`)

  const isContactActive = contact =>
    Boolean(
      contact?.id && location.pathname.includes(`/messages/${contact.id}`)
    )

  const applyDirectContactUpdate = useCallback(
    rawContact => {
      const mapped = mapDirectContact(rawContact, currentUserId, currentUser)
      if (!mapped.id) return

      const senderId = String(rawContact?.last_message?.sender?.id || "")
      const isOwnMessage = Boolean(
        currentUserId && senderId && senderId === String(currentUserId)
      )

      // Single DM (no project) → Direct Messages sidebar + floating chat.
      if (isStandaloneDirectChat(mapped, rawContact)) {
        if (!mapped.userId) return

        setDmMembers(prev => {
          const existing = prev.find(
            item =>
              String(item.userId) === String(mapped.userId) ||
              String(item.contextId || item.id) === String(mapped.id)
          )
          const dockOpen = isExpandedDmDockOpen({
            id: mapped.id,
            contextId: mapped.id,
            userId: mapped.userId,
          })
          const hasUnread = isOwnMessage
            ? Boolean(existing?.hasUnread)
            : !dockOpen
          const next = {
            id: mapped.id,
            memberId: existing?.memberId || null,
            contextId: mapped.id,
            projectId: null,
            projectName: "",
            userId: mapped.userId,
            name: mapped.name || existing?.name || "Unknown",
            initials:
              mapped.initials ||
              existing?.initials ||
              getContactInitials(mapped.name || existing?.name),
            avatar: mapped.avatar || existing?.avatar || "",
            isGroup: false,
            subject: "",
            participants: (mapped.participants || []).length
              ? mapped.participants
              : existing?.participants || [],
            hasUnread,
          }
          const rest = prev.filter(
            item =>
              String(item.userId) !== String(next.userId) &&
              String(item.contextId || item.id) !== String(mapped.id)
          )
          return [next, ...rest]
        })

        // Keep Project Messages free of standalone DMs.
        setDirectContacts(prev =>
          prev.filter(
            item =>
              String(item.id) !== String(mapped.id) &&
              !(
                !item.projectId &&
                !item.isGroup &&
                item.userId &&
                String(item.userId) === String(mapped.userId)
              )
          )
        )
        return
      }

      const isOpen = location.pathname.includes(`/messages/${mapped.id}`)

      setDirectContacts(prev => {
        const existing = prev.find(
          item => String(item.id) === String(mapped.id)
        )
        const merged = mergeDirectContactIdentity(
          existing,
          mapped,
          currentUserId
        )
        const next = {
          ...merged,
          projectName:
            merged.projectName ||
            projectNameCacheRef.current[String(merged.projectId || "")] ||
            existing?.projectName ||
            "",
          hasUnread: !isOwnMessage && !isOpen,
        }
        const rest = prev.filter(
          contact => String(contact.id) !== String(next.id)
        )
        return [next, ...rest]
      })

      if (mapped.projectId && !mapped.projectName) {
        fetchProjectNamesByIds([mapped.projectId]).then(names => {
          setDirectContacts(prev => applyProjectNames(prev, names))
        })
      }
    },
    [
      currentUser,
      currentUserId,
      fetchProjectNamesByIds,
      isExpandedDmDockOpen,
      location.pathname,
    ]
  )

  useEffect(
    () => subscribeDirectContactUpdates(applyDirectContactUpdate),
    [subscribeDirectContactUpdates, applyDirectContactUpdate]
  )

  // Invite accepted / member activated → refetch Direct Messages (my-members).
  useEffect(() => {
    if (typeof subscribeChatMembersUpdated !== "function") return undefined
    return subscribeChatMembersUpdated(() => {
      fetchDmMembers({
        page: 1,
        append: false,
        search: debouncedDmSearch,
      })
    })
  }, [subscribeChatMembersUpdated, fetchDmMembers, debouncedDmSearch])

  useEffect(() => {
    const openId = String(
      location.pathname.match(/\/messages\/([^/]+)/)?.[1] || ""
    )
    if (!openId) return
    setDirectContacts(prev => {
      let changed = false
      const next = prev.map(contact => {
        if (String(contact.id) !== openId || !contact.hasUnread) return contact
        changed = true
        return { ...contact, hasUnread: false }
      })
      return changed ? next : prev
    })
  }, [location.pathname])

  useEffect(() => {
    const openIds = new Set(
      (openChats || [])
        .filter(chat => !chat.minimized)
        .flatMap(chat =>
          [chat.id, chat.contextId].filter(Boolean).map(String)
        )
    )
    const openUserIds = new Set(
      (openChats || [])
        .filter(chat => !chat.minimized && chat.userId != null)
        .map(chat => String(chat.userId))
    )
    if (!openIds.size && !openUserIds.size) return
    setDmMembers(prev => {
      let changed = false
      const next = prev.map(contact => {
        const contextKey = String(contact.contextId || contact.id || "")
        const userKey =
          contact.userId != null ? String(contact.userId) : ""
        const isOpen =
          (contextKey && openIds.has(contextKey)) ||
          (userKey && openUserIds.has(userKey))
        if (!isOpen || !contact.hasUnread) return contact
        changed = true
        return { ...contact, hasUnread: false }
      })
      return changed ? next : prev
    })
  }, [openChats])

  const clearContactUnread = useCallback((contactId, userId = null) => {
    if (!contactId && userId == null) return
    const id = contactId != null ? String(contactId) : ""
    const uid = userId != null ? String(userId) : ""
    setDirectContacts(prev =>
      prev.map(item =>
        id && String(item.id) === id
          ? { ...item, hasUnread: false }
          : item
      )
    )
    setDmMembers(prev =>
      prev.map(item => {
        const match =
          (id &&
            (String(item.id) === id ||
              String(item.contextId || "") === id)) ||
          (uid && String(item.userId) === uid)
        return match ? { ...item, hasUnread: false } : item
      })
    )
  }, [])

  const openProjectChat = useCallback(
    contact => {
      if (!contact?.id) return
      const projectId =
        contact.projectId || getProjectIdFromPath(location.pathname)
      if (!projectId) return
      clearContactUnread(contact.id)
      navigate(`/projects/${projectId}/messages/${contact.id}`)
    },
    [clearContactUnread, location.pathname, navigate]
  )

  const openDirectChat = useCallback(
    async contact => {
      if (!contact?.userId || contact.isGroup) return

      let chatId = contact.contextId || null

      if (!chatId) {
        try {
          const response = await post(
            endpoints.getOrCreateDirectChat(contact.userId),
            {},
            { skipErrorToast: false }
          )
          chatId = response?.data?.result?.id || null
          if (chatId) {
            setDmMembers(prev =>
              prev.map(item =>
                String(item.userId) === String(contact.userId)
                  ? { ...item, contextId: chatId, id: chatId }
                  : item
              )
            )
          }
        } catch {
          return
        }
      }

      if (!chatId) return

      clearContactUnread(chatId, contact.userId)
      openFloatingDirectChat({
        ...contact,
        id: chatId,
        contextId: chatId,
        projectName: "",
        projectId: contact.projectId || null,
      })
    },
    [clearContactUnread, openFloatingDirectChat]
  )

  const resolveProjectName = useCallback(
    contact =>
      contact.projectName ||
      recentProjects.find(
        project => String(project.id) === String(contact.projectId)
      )?.name ||
      "",
    [recentProjects]
  )

  const renderMessageContact = (user, { onOpen, showProject = true }) => {
    const dockActive = !showProject && isExpandedDmDockOpen(user)
    const active = showProject ? isContactActive(user) : dockActive
    const unread = Boolean(user.hasUnread) && !active
    const projectName = showProject ? resolveProjectName(user) : ""
    const groupFaces = user.isGroup
      ? (user.participants || []).slice(0, 4)
      : []

    return (
      <button
        key={user.userId || user.id}
        type="button"
        className={`dm-item${active ? " dm-item--active" : ""}${
          unread ? " dm-item--unread" : ""
        }${user.isGroup ? " dm-item--group" : ""}`}
        onClick={() => onOpen(user)}
        aria-label={`Open chat with ${user.name}${
          projectName ? ` on ${projectName}` : ""
        }`}
        aria-current={active ? "page" : undefined}
      >
        <div className="dm-item__avatar-wrapper">
          {user.isGroup && groupFaces.length > 1 ? (
            <div className="dm-item__group-avatar" aria-hidden="true">
              {groupFaces.map((person, index) => {
                const src = person?.avatar || ""
                const label = person?.name || ""
                return src ? (
                  <img
                    key={person?.id || label || index}
                    src={src}
                    alt=""
                    className="dm-item__group-avatar-img"
                  />
                ) : (
                  <span
                    key={person?.id || label || index}
                    className="dm-item__group-avatar-img dm-item__group-avatar-img--fallback"
                  >
                    {getContactInitials(label)}
                  </span>
                )
              })}
            </div>
          ) : (
            <div className="dm-item__avatar">
              {typeof user.avatar === "string" && user.avatar ? (
                <img src={user.avatar} alt="" />
              ) : (
                user.initials
              )}
            </div>
          )}
          {!user.isGroup && user.userId ? (
            <OnlineStatus
              userId={user.userId}
              variant="dot"
              className="dm-item__status"
            />
          ) : null}
        </div>
        <div className="dm-item__meta">
          <div className="dm-item__name">{user.name}</div>
          {projectName ? (
            <div className="dm-item__project">{projectName}</div>
          ) : null}
        </div>
        {unread ? (
          <span className="dm-item__unread" aria-hidden="true" />
        ) : null}
      </button>
    )
  }

  const renderMessageSkeletons = (prefix, { showProject = true } = {}) =>
    Array.from({ length: DIRECT_CONTACTS_SKELETON_COUNT }).map((_, index) => (
      <div
        key={`${prefix}-skeleton-${index}`}
        className="dm-item dm-item--skeleton"
        aria-hidden="true"
      >
        <div className="dm-item__avatar-wrapper">
          <div className="dm-item__avatar amok-skeleton" />
        </div>
        <div className="dm-item__meta">
          <div className="dm-item__skeleton-name amok-skeleton" />
          {showProject ? (
            <div className="dm-item__skeleton-project amok-skeleton" />
          ) : null}
        </div>
      </div>
    ))

  const renderMessageSection = ({
    searchOpen,
    setSearchOpen,
    searchQuery,
    setSearchQuery,
    expanded,
    setExpanded,
    title,
    collapsedHeading,
    placeholder,
    filtered,
    onOpen,
    skeletonPrefix,
    isLoading,
    showProject = true,
    listRef = null,
    sentinelRef = null,
    hasMore = false,
    isLoadingMore = false,
  }) => (
    <div className="sidebar-section__block">
      <div className="sidebar-section__header">
        {searchOpen ? (
          <div className="sidebar-search">
            <img
              src={ICONS.searchSidebar}
              alt=""
              className="sidebar-search__icon"
            />
            <input
              type="text"
              className="sidebar-search__input"
              placeholder={placeholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              className="sidebar-search__close"
              onClick={() => {
                setSearchOpen(false)
                setSearchQuery("")
              }}
            >
              ×
            </button>
          </div>
        ) : (
          <>
            <div
              className="sidebar-section__header-left"
              onClick={() => setExpanded(!expanded)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setExpanded(!expanded)
                }
              }}
              role="button"
              tabIndex={0}
            >
              <span>{title}</span>
              <img
                src={ICONS.chevronSidebar}
                alt=""
                className={`sidebar-section__chevron ${
                  expanded ? "sidebar-section__chevron--expanded" : ""
                }`}
              />
            </div>
            <div className="sidebar-section__actions">
              <button
                type="button"
                className="sidebar-section__icon-btn"
                onClick={() => {
                  setSearchOpen(true)
                  setExpanded(true)
                }}
              >
                <img
                  src={ICONS.searchSidebar}
                  alt=""
                  className="sidebar-section__action-icon"
                />
              </button>
            </div>
          </>
        )}
      </div>

      {expanded ? (
        <>
          <div className="sidebar-collapsed-heading">{collapsedHeading}</div>
          <div
            ref={listRef || undefined}
            className="dm-list"
            aria-busy={isLoading || isLoadingMore || undefined}
          >
            {isLoading
              ? renderMessageSkeletons(skeletonPrefix, { showProject })
              : filtered.map(user =>
                  renderMessageContact(user, { onOpen, showProject })
                )}
            {!isLoading && isLoadingMore
              ? renderMessageSkeletons(`${skeletonPrefix}-more`, {
                  showProject,
                }).slice(0, 2)
              : null}
            {!isLoading && hasMore ? (
              <div ref={sentinelRef || undefined} aria-hidden="true" />
            ) : null}
            {!isLoading && searchQuery && filtered.length === 0 && (
              <div className="sidebar-search-empty">No results found</div>
            )}
            {!isLoading && !searchQuery && filtered.length === 0 && (
              <div className="sidebar-search-empty">No conversations yet</div>
            )}
          </div>
        </>
      ) : null}
    </div>
  )

  return (
    <React.Fragment>
      <div className="sidebar-section">
        <div className="sidebar-section__block">
          <div className="sidebar-section__header">
            {projectSearchOpen ? (
              <div className="sidebar-search">
                <img
                  src={ICONS.searchSidebar}
                  alt=""
                  className="sidebar-search__icon"
                />
                <input
                  type="text"
                  className="sidebar-search__input"
                  placeholder="Search projects..."
                  value={projectSearchQuery}
                  onChange={e => setProjectSearchQuery(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className="sidebar-search__close"
                  onClick={() => {
                    setProjectSearchOpen(false)
                    setProjectSearchQuery("")
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                <div
                  className="sidebar-section__header-left"
                  onClick={() => setProjectsExpanded(!projectsExpanded)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      setProjectsExpanded(!projectsExpanded)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span>Recent Projects</span>
                  <img
                    src={ICONS.chevronSidebar}
                    alt=""
                    className={`sidebar-section__chevron ${
                      projectsExpanded
                        ? "sidebar-section__chevron--expanded"
                        : ""
                    }`}
                  />
                </div>
                <div className="sidebar-section__actions">
                  <button
                    type="button"
                    className="sidebar-section__icon-btn"
                    onClick={() => {
                      setProjectSearchOpen(true)
                      setProjectsExpanded(true)
                    }}
                  >
                    <img
                      src={ICONS.searchSidebar}
                      alt=""
                      className="sidebar-section__action-icon"
                    />
                  </button>
                  <button
                    type="button"
                    className="sidebar-section__icon-btn"
                    onClick={() => navigate("/projects/create")}
                  >
                    <img
                      src={ICONS.addCircleSidebar}
                      alt=""
                      className="sidebar-section__action-icon"
                    />
                  </button>
                  <button
                    type="button"
                    className="sidebar-section__icon-btn"
                    onClick={() => navigate("/projects")}
                  >
                    <img
                      src={ICONS.expandSidebar}
                      alt=""
                      className="sidebar-section__action-icon"
                    />
                  </button>
                </div>
              </>
            )}
          </div>

          {showProjectsList && (
            <>
              <div className="sidebar-collapsed-heading">Projects</div>
              <div
                className="project-list"
                aria-busy={isRecentProjectsLoading || undefined}
              >
                {isRecentProjectsLoading
                  ? Array.from({ length: RECENT_PROJECTS_SKELETON_COUNT }).map(
                      (_, index) => (
                        <div
                          key={`project-skeleton-${index}`}
                          className="project-item project-item--skeleton"
                          aria-hidden="true"
                        >
                          <div className="project-item__avatar amok-skeleton" />
                          <div className="project-item__info">
                            <div className="project-item__skeleton-title amok-skeleton" />
                          </div>
                        </div>
                      )
                    )
                  : filteredProjects.map(project => {
                      const active = isProjectActive(project.id)
                      return (
                        <div
                          key={project.id}
                          className={`project-item ${
                            active ? "project-item--active" : ""
                          }`}
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            navigate(`/projects/${project.id}/overview`)
                          }
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault()
                              navigate(`/projects/${project.id}/overview`)
                            }
                          }}
                        >
                          <div
                            className="project-item__avatar"
                            style={{
                              background: project.backgroundColor,
                              color: project.textColor,
                            }}
                          >
                            {project.initials}
                          </div>
                          <div className="project-item__info">
                            <div
                              className={`project-item__title ${
                                active ? "project-item__title--active" : ""
                              }`}
                            >
                              {project.name}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                {!isRecentProjectsLoading &&
                  projectSearchQuery &&
                  filteredProjects.length === 0 && (
                    <div className="sidebar-search-empty">No results found</div>
                  )}
                {!isRecentProjectsLoading &&
                  (!projectSearchQuery || filteredProjects.length > 0) && (
                    <button
                      type="button"
                      className="view-all"
                      onClick={() => navigate("/projects")}
                    >
                      View All
                      <img
                        src={ICONS.arrowRight12}
                        alt=""
                        className="view-all__icon"
                      />
                    </button>
                  )}
              </div>
              <div
                className="view-all-collapsed"
                onClick={() => navigate("/projects")}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    navigate("/projects")
                  }
                }}
                role="button"
                tabIndex={0}
              >
                View all
              </div>
            </>
          )}
        </div>

        <div className="sidebar-section__divider" />

        {renderMessageSection({
          searchOpen: dmSearchOpen,
          setSearchOpen: setDmSearchOpen,
          searchQuery: dmSearchQuery,
          setSearchQuery: setDmSearchQuery,
          expanded: showDmList,
          setExpanded: setDmExpanded,
          title: "Direct Messages",
          collapsedHeading: "Direct",
          placeholder: "Search people...",
          filtered: filteredDirectUsers,
          onOpen: openDirectChat,
          skeletonPrefix: "dm",
          isLoading: isDmMembersLoading,
          showProject: false,
          listRef: dmListRef,
          sentinelRef: dmSentinelRef,
          hasMore: dmHasMore,
          isLoadingMore: isDmMembersLoadingMore,
        })}

        <div className="sidebar-section__divider" />

        {renderMessageSection({
          searchOpen: pmSearchOpen,
          setSearchOpen: setPmSearchOpen,
          searchQuery: pmSearchQuery,
          setSearchQuery: setPmSearchQuery,
          expanded: showPmList,
          setExpanded: setPmExpanded,
          title: "Project Messages",
          collapsedHeading: "Projects",
          placeholder: "Search project chats...",
          filtered: filteredProjectUsers,
          onOpen: openProjectChat,
          skeletonPrefix: "pm",
          isLoading: isDirectContactsLoading,
          showProject: true,
          listRef: pmListRef,
          sentinelRef: pmSentinelRef,
          hasMore: pmHasMore,
          isLoadingMore: isDirectContactsLoadingMore,
        })}
      </div>
    </React.Fragment>
  )
}

export default withRouter(withTranslation()(SidebarContent))
